import React, { useState } from "react";
import { ExternalLink, Heart, Bell, BellOff, Copy, Check, Tag } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function ResultsView({ result, onRequireAuth }) {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [alertOn, setAlertOn] = useState(result?.price_alert || false);
  const [savedFav, setSavedFav] = useState({}); // merchantId -> favId

  if (!result) return null;
  const a = result.analysis;

  const thumb = result.source_url
    ? null
    : `data:image/jpeg;base64,${result.thumbnail_b64}`;
  const cover =
    thumb ||
    (result.source_url ? null : null);

  const copyKw = async () => {
    await navigator.clipboard.writeText(a.search_keywords);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  const togglePriceAlert = async () => {
    if (!user) return onRequireAuth?.();
    try {
      const { data } = await api.post(`/searches/${result.id}/price-alert`);
      setAlertOn(data.price_alert);
      toast.success(data.price_alert ? "Alerte prix activée" : "Alerte désactivée");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveFavorite = async (merchant) => {
    if (!user) return onRequireAuth?.();
    try {
      const { data } = await api.post("/favorites", {
        search_id: result.id,
        merchant: merchant.name,
        title: a.title,
        url: merchant.url,
        thumbnail: thumb ? null : null,
      });
      setSavedFav((s) => ({ ...s, [merchant.id]: data.id }));
      toast.success(`Ajouté aux favoris — ${merchant.name}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="results-view" className="space-y-6 animate-fade-up">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: visual */}
        <div className="lg:col-span-2">
          <div className="card-brut overflow-hidden">
            {thumb ? (
              <img
                src={thumb}
                alt={a.title}
                className="w-full aspect-[4/5] object-cover"
              />
            ) : (
              <div className="aspect-[4/5] bg-mist grid place-items-center">
                <span className="tape-label">Image source</span>
              </div>
            )}
            <div className="p-4 border-t border-ink/10">
              <div className="tape-label text-poppy mb-1">CATÉGORIE</div>
              <div className="font-display font-bold text-lg capitalize">
                {a.category === "clothing"
                  ? "Mode & vêtements"
                  : a.category === "home_decor"
                  ? "Déco & maison"
                  : "Objet"}
                {a.subcategory ? ` · ${a.subcategory}` : ""}
              </div>
            </div>
          </div>
        </div>

        {/* Right: analysis */}
        <div className="lg:col-span-3 space-y-5">
          <div>
            <div className="tape-label text-poppy mb-1">ANALYSE IA</div>
            <h2 className="font-display font-black text-3xl md:text-4xl leading-tight">
              {a.title}
            </h2>
            <p className="mt-3 text-ink/70 text-base leading-relaxed">{a.description}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Style" value={a.style} />
            <Stat label="Couleurs" value={(a.colors || []).join(", ")} />
            <Stat label="Matière" value={(a.materials || []).join(", ")} />
            <Stat label="Prix estimé" value={a.estimated_price_range} />
          </div>

          {a.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {a.tags.map((t) => (
                <span
                  key={t}
                  className="px-2.5 py-1 text-xs font-bold uppercase tracking-wider border border-ink/30 bg-white"
                >
                  #{t}
                </span>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <button
              data-testid="copy-keywords-button"
              onClick={copyKw}
              className="btn-ghost-brut px-3 py-2 text-xs flex items-center gap-1.5"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copié" : `Copier les mots-clés`}
            </button>
            <button
              data-testid="toggle-price-alert"
              onClick={togglePriceAlert}
              className={`px-3 py-2 text-xs flex items-center gap-1.5 border border-ink ${
                alertOn ? "bg-electric text-ink" : "bg-white hover:bg-mist/40"
              } font-bold`}
            >
              {alertOn ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
              {alertOn ? "Alerte prix ON" : "Activer alerte prix"}
            </button>
          </div>
        </div>
      </div>

      {/* Merchants */}
      <section>
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="tape-label text-poppy">02 / OÙ ACHETER</div>
            <h3 className="font-display font-black text-2xl md:text-3xl">
              Magasins suggérés
            </h3>
          </div>
          <div className="tape-label hidden sm:block">
            <Tag className="w-3 h-3 inline mr-1" />
            {a.search_keywords}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {result.merchants.map((m) => (
            <div
              key={m.id}
              data-testid={`merchant-card-${m.id}`}
              className="card-brut p-4 flex flex-col gap-3"
            >
              <div className="flex items-center justify-between">
                <div
                  className="w-10 h-10 grid place-items-center font-display font-black text-linen border border-ink"
                  style={{ background: m.color }}
                >
                  {m.name[0]}
                </div>
                <button
                  data-testid={`save-fav-${m.id}`}
                  onClick={() => saveFavorite(m)}
                  className="p-2 border border-ink/30 hover:bg-ink hover:text-linen transition"
                  aria-label="Ajouter aux favoris"
                >
                  <Heart
                    className={`w-4 h-4 ${
                      savedFav[m.id] ? "fill-poppy text-poppy" : ""
                    }`}
                  />
                </button>
              </div>
              <div>
                <div className="font-display font-black text-lg">{m.name}</div>
                <div className="text-xs text-ink/60">
                  Recherche : <span className="font-bold">{a.search_keywords}</span>
                </div>
              </div>
              <a
                data-testid={`merchant-open-${m.id}`}
                href={m.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-brut px-3 py-2 text-xs flex items-center justify-center gap-1.5"
              >
                Voir sur {m.name} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  if (!value) return null;
  return (
    <div className="border border-ink/15 bg-white p-3">
      <div className="tape-label text-ink/50">{label}</div>
      <div className="text-sm font-bold mt-0.5 line-clamp-2">{value}</div>
    </div>
  );
}
